import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Trophy, Video, Calendar, User, Shield, MessageCircle, MapPin, Plane, Users, Bell, Crown, X, Flag, UserPlus, Menu, CalendarCheck, Tag, PlusCircle, Search, Download, LifeBuoy, Tv, Rss, Camera, Star, Trash2, Briefcase } from 'lucide-react';
import { triggerInstall, isStandalone } from '../utils/pwaInstall';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const Navigation = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showMobileMore, setShowMobileMore] = useState(false);
  const mobileNotifRef = useRef(null);
  const desktopNotifRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    if (user) { fetchUnread(); fetchChatUnread(); }
    // Poll every 10s so new notifications (chat/PB/round-tags) appear on the bell quickly
    const interval = setInterval(() => { if (user) { fetchUnread(); fetchChatUnread(); } }, 10000);
    // Also refresh when the tab becomes visible again — catches messages while backgrounded
    const onVisible = () => { if (document.visibilityState === 'visible' && user) { fetchUnread(); fetchChatUnread(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [user]);

  useEffect(() => {
    const handleClick = (e) => {
      const m = mobileNotifRef.current;
      const d = desktopNotifRef.current;
      const insideMobile = m && m.contains(e.target);
      const insideDesktop = d && d.contains(e.target);
      if (!insideMobile && !insideDesktop) setShowNotifs(false);
    };
    // Use 'click' instead of 'mousedown' to avoid firing BEFORE the bell's onClick handler
    // (mousedown fires before click; if the tap target's ref check has any edge-case timing
    //  issue, the panel could close before it opens).
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const fetchUnread = async () => {
    try {
      const res = await fetch(`${API}/notifications/unread-count`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setUnreadCount(d.count); }
    } catch (e) { console.error('fetchUnread failed', e); }
  };

  const fetchChatUnread = async () => {
    try {
      const res = await fetch(`${API}/chat/unread-count`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setChatUnreadCount(d.count); }
    } catch (e) { console.error('fetchChatUnread failed', e); }
  };

  const openNotifs = async (e) => {
    // Prevent the document-level click-outside handler from immediately closing the panel
    if (e && e.stopPropagation) e.stopPropagation();
    // Functional update — avoids any stale-closure edge case
    let willBeOpen = false;
    setShowNotifs(prev => {
      willBeOpen = !prev;
      return willBeOpen;
    });
    if (willBeOpen) {
      try {
        const res = await fetch(`${API}/notifications?limit=15`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          // API returns array; guard against unexpected shapes
          setNotifications(Array.isArray(data) ? data : (data?.notifications || []));
        }
      } catch (err) { console.error('openNotifs failed', err); }
    }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API}/notifications/read-all`, { method: 'PUT', credentials: 'include' });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) { console.error('markAllRead failed', e); }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all notifications? This cannot be undone.')) return;
    try {
      await fetch(`${API}/notifications`, { method: 'DELETE', credentials: 'include' });
      setNotifications([]);
      setUnreadCount(0);
    } catch (e) { console.error('clearAll failed', e); }
  };

  const deleteOne = async (e, notif) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await fetch(`${API}/notifications/${notif.notification_id}`, { method: 'DELETE', credentials: 'include' });
      setNotifications(prev => prev.filter(n => n.notification_id !== notif.notification_id));
      if (!notif.read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { console.error('deleteOne failed', e); }
  };

  const clickNotif = async (notif) => {
    if (!notif.read) {
      fetch(`${API}/notifications/${notif.notification_id}/read`, { method: 'PUT', credentials: 'include' });
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, read: true } : n));
    }
    setShowNotifs(false);
    if (notif.link) navigate(notif.link);
  };

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <>
      {/* Mobile Top Bar — shifts down when PWA install banner is showing (see PWAInstallPrompt.jsx) */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-bundle-cream-dark z-50 mobile-topbar" data-testid="mobile-top-bar">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <img src="/logo.png" alt="Bundle Golf" className="h-16 w-auto mix-blend-multiply" />
          </div>
          <div className="flex items-center gap-1">
            {/* Chat icon */}
            <button
              data-testid="mobile-topbar-chat"
              onClick={() => navigate('/chat')}
              className="relative p-2 rounded-lg hover:bg-bundle-cream-dark/60 transition-colors"
            >
              <MessageCircle className="w-5 h-5 text-bundle-green" />
              {chatUnreadCount > 0 && (
                <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center" data-testid="chat-unread-badge-mobile">
                  {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                </span>
              )}
            </button>
            {/* Notification bell */}
            <div className="relative" ref={mobileNotifRef}>
              <button
                data-testid="mobile-notification-bell"
                onClick={(e) => openNotifs(e)}
                className="relative p-2 rounded-lg hover:bg-bundle-cream-dark/60 transition-colors"
              >
                <Bell className="w-5 h-5 text-bundle-green" />
                {unreadCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifs && (
                <div className="absolute right-0 top-12 w-80 bg-white border border-bundle-cream-dark rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="mobile-notification-panel">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-bundle-cream-dark bg-bundle-cream/30">
                    <h3 className="font-heading font-bold text-sm text-bundle-green">Notifications</h3>
                    <div className="flex items-center gap-3">
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllRead}
                          data-testid="mark-all-read-btn-mobile"
                          className="font-body text-xs text-bundle-gold hover:underline"
                        >
                          Mark all read
                        </button>
                      )}
                      {notifications.length > 0 && (
                        <button
                          onClick={clearAll}
                          data-testid="clear-all-btn-mobile"
                          className="font-body text-xs text-red-500 hover:underline"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center">
                        <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="font-body text-sm text-gray-500">No notifications yet</p>
                      </div>
                    ) : notifications.map(n => (
                      <div
                        key={n.notification_id}
                        className={`relative group border-b border-bundle-cream-dark/50 transition-colors flex items-stretch ${
                          !n.read ? 'bg-bundle-gold/5' : ''
                        }`}
                      >
                        <button
                          onClick={() => clickNotif(n)}
                          data-testid={`notif-row-${n.notification_id}`}
                          className="flex-1 text-left px-4 py-3 hover:bg-bundle-cream/30 flex items-start gap-3 min-w-0"
                        >
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                            n.type === 'personal_record' ? 'bg-bundle-gold/15' :
                            n.type === 'event_signup' ? 'bg-blue-50' :
                            n.type === 'new_member' ? 'bg-green-50' :
                            n.type === 'live_round_started' ? 'bg-red-50' :
                            'bg-bundle-green/10'
                          }`}>
                            {n.type === 'personal_record' ? <Crown className="w-4 h-4 text-bundle-gold" /> :
                             n.type === 'event_signup' ? <Calendar className="w-4 h-4 text-blue-500" /> :
                             n.type === 'new_member' ? <UserPlus className="w-4 h-4 text-green-600" /> :
                             n.type === 'live_round_started' ? <Flag className="w-4 h-4 text-red-500" /> :
                             <Bell className="w-4 h-4 text-bundle-green" />}
                          </div>
                          <div className="flex-1 min-w-0 pr-6">
                            <p className={`font-body text-sm ${!n.read ? 'font-semibold text-bundle-green' : 'text-gray-700'}`}>
                              {n.message}
                            </p>
                            <p className="font-body text-[10px] text-gray-400 mt-0.5">{timeAgo(n.created_at)}</p>
                          </div>
                          {!n.read && <span className="flex-shrink-0 w-2 h-2 bg-bundle-gold rounded-full mt-2" />}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => deleteOne(e, n)}
                          data-testid={`notif-delete-${n.notification_id}`}
                          aria-label="Delete notification"
                          className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full opacity-60 hover:opacity-100 transition-all"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* More menu */}
            <button
              data-testid="mobile-topbar-more"
              onClick={() => setShowMobileMore(!showMobileMore)}
              className="p-2 rounded-lg hover:bg-bundle-cream-dark/60 transition-colors"
            >
              <Menu className="w-5 h-5 text-bundle-green" />
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Navigation */}
      <nav className="hidden md:block fixed top-0 left-0 right-0 bg-white border-b border-bundle-cream-dark z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div 
              className="flex items-center gap-3 cursor-pointer" 
              onClick={() => navigate('/')}
            >
              <img 
                src="/logo.png" 
                alt="The Bundle Golf Club" 
                className="h-36 w-auto mix-blend-multiply"
              />
            </div>

            {/* Nav Links */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigate('/dashboard')}
                className={`px-3 py-2 rounded-lg font-body text-sm transition-colors ${
                  isActive('/dashboard') 
                    ? 'bg-bundle-green text-white' 
                    : 'text-bundle-green hover:bg-bundle-cream-dark'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => navigate('/nearby-golfers')}
                className={`px-3 py-2 rounded-lg font-body text-sm transition-colors ${
                  isActive('/nearby-golfers') 
                    ? 'bg-bundle-green text-white' 
                    : 'text-bundle-green hover:bg-bundle-cream-dark'
                }`}
              >
                Golfers Near Me
              </button>
              <button
                onClick={() => navigate('/chat')}
                className={`relative px-3 py-2 rounded-lg font-body text-sm transition-colors ${
                  isActive('/chat') 
                    ? 'bg-bundle-green text-white' 
                    : 'text-bundle-green hover:bg-bundle-cream-dark'
                }`}
              >
                Chat
                {chatUnreadCount > 0 && (
                  <span className="ml-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center" data-testid="chat-unread-badge-desktop">
                    {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => navigate('/leaderboard')}
                className={`px-3 py-2 rounded-lg font-body text-sm transition-colors ${
                  isActive('/leaderboard') 
                    ? 'bg-bundle-green text-white' 
                    : 'text-bundle-green hover:bg-bundle-cream-dark'
                }`}
              >
                Leaderboard
              </button>
              <button
                onClick={() => navigate('/events')}
                className={`px-3 py-2 rounded-lg font-body text-sm transition-colors ${
                  isActive('/events') 
                    ? 'bg-bundle-green text-white' 
                    : 'text-bundle-green hover:bg-bundle-cream-dark'
                }`}
              >
                Events
              </button>
              <button
                onClick={() => navigate('/golf-trips')}
                className={`px-3 py-2 rounded-lg font-body text-sm transition-colors ${
                  isActive('/golf-trips') 
                    ? 'bg-bundle-green text-white' 
                    : 'text-bundle-green hover:bg-bundle-cream-dark'
                }`}
              >
                Travel
              </button>
              <button
                onClick={() => navigate('/gear')}
                className={`px-3 py-2 rounded-lg font-body text-sm transition-colors ${
                  isActive('/gear') 
                    ? 'bg-bundle-green text-white' 
                    : 'text-bundle-green hover:bg-bundle-cream-dark'
                }`}
              >
                Marketplace
              </button>
              <button
                onClick={() => navigate('/feed')}
                data-testid="nav-feed"
                className={`px-3 py-2 rounded-lg font-body text-sm transition-colors ${
                  isActive('/feed') 
                    ? 'bg-bundle-green text-white' 
                    : 'text-bundle-green hover:bg-bundle-cream-dark'
                }`}
              >
                Feed
              </button>
              <button
                onClick={() => navigate('/members')}
                data-testid="nav-members"
                className={`px-3 py-2 rounded-lg font-body text-sm transition-colors ${
                  isActive('/members') 
                    ? 'bg-bundle-green text-white' 
                    : 'text-bundle-green hover:bg-bundle-cream-dark'
                }`}
              >
                Members
              </button>
              <button
                onClick={() => navigate('/network')}
                data-testid="nav-network"
                className={`px-3 py-2 rounded-lg font-body text-sm transition-colors ${
                  isActive('/network') 
                    ? 'bg-bundle-green text-white' 
                    : 'text-bundle-green hover:bg-bundle-cream-dark'
                }`}
              >
                Network
              </button>
              {user?.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className={`px-4 py-2 rounded-lg font-body text-sm transition-colors ${
                    isActive('/admin') 
                      ? 'bg-bundle-gold text-bundle-green-dark' 
                      : 'text-bundle-gold hover:bg-bundle-gold/10'
                  }`}
                >
                  <Shield className="w-4 h-4 inline mr-1" />
                  Director
                </button>
              )}
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-2">
              {/* Notification Bell */}
              <div className="relative" ref={desktopNotifRef}>
                <button
                  data-testid="notification-bell"
                  onClick={(e) => openNotifs(e)}
                  className="relative p-2 rounded-lg hover:bg-bundle-cream-dark transition-colors"
                >
                  <Bell className="w-5 h-5 text-bundle-green" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="unread-badge">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                {showNotifs && (
                  <div className="absolute right-0 top-12 w-80 bg-white border border-bundle-cream-dark rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notification-panel">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-bundle-cream-dark bg-bundle-cream/30">
                      <h3 className="font-heading font-bold text-sm text-bundle-green">Notifications</h3>
                      <div className="flex items-center gap-3">
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllRead}
                            data-testid="mark-all-read-btn"
                            className="font-body text-xs text-bundle-gold hover:underline"
                          >
                            Mark all read
                          </button>
                        )}
                        {notifications.length > 0 && (
                          <button
                            onClick={clearAll}
                            data-testid="clear-all-btn"
                            className="font-body text-xs text-red-500 hover:underline"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-6 text-center">
                          <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                          <p className="font-body text-sm text-gray-500">No notifications yet</p>
                        </div>
                      ) : notifications.map(n => (
                        <div
                          key={n.notification_id}
                          className={`relative group border-b border-bundle-cream-dark/50 transition-colors flex items-stretch ${
                            !n.read ? 'bg-bundle-gold/5' : ''
                          }`}
                        >
                          <button
                            data-testid={`notif-${n.notification_id}`}
                            onClick={() => clickNotif(n)}
                            className="flex-1 text-left px-4 py-3 hover:bg-bundle-cream/30 flex items-start gap-3 min-w-0"
                          >
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                              n.type === 'personal_record' ? 'bg-bundle-gold/15' :
                              n.type === 'event_signup' ? 'bg-blue-50' :
                              n.type === 'new_member' ? 'bg-green-50' :
                              n.type === 'live_round_started' ? 'bg-red-50' :
                              'bg-bundle-green/10'
                            }`}>
                              {n.type === 'personal_record' ? (
                                <Crown className="w-4 h-4 text-bundle-gold" />
                              ) : n.type === 'event_signup' ? (
                                <Calendar className="w-4 h-4 text-blue-500" />
                              ) : n.type === 'new_member' ? (
                                <UserPlus className="w-4 h-4 text-green-600" />
                              ) : n.type === 'live_round_started' ? (
                                <Flag className="w-4 h-4 text-red-500" />
                              ) : (
                                <Bell className="w-4 h-4 text-bundle-green" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0 pr-6">
                              <p className={`font-body text-sm ${!n.read ? 'font-semibold text-bundle-green' : 'text-gray-700'}`}>
                                {n.message}
                              </p>
                              <p className="font-body text-[10px] text-gray-400 mt-0.5">{timeAgo(n.created_at)}</p>
                            </div>
                            {!n.read && <span className="flex-shrink-0 w-2 h-2 bg-bundle-gold rounded-full mt-2" />}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => deleteOne(e, n)}
                            data-testid={`notif-delete-desktop-${n.notification_id}`}
                            aria-label="Delete notification"
                            className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full opacity-60 hover:opacity-100 transition-all"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-bundle-cream-dark transition-colors"
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 bg-bundle-green rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                )}
                <span className="font-body text-sm text-bundle-green">{user?.name?.split(' ')[0]}</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile More Menu Overlay */}
      {showMobileMore && (
        <div className="md:hidden fixed inset-0 z-[60]" onClick={() => setShowMobileMore(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="absolute bottom-20 left-3 right-3 bg-white rounded-2xl border border-bundle-cream-dark shadow-2xl p-4 pb-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-heading font-bold text-bundle-green text-sm">More</span>
              <button onClick={() => setShowMobileMore(false)} className="p-1 rounded-full hover:bg-gray-100">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[
                { path: '/leaderboard', icon: Trophy, label: 'Leaderboard' },
                { path: '/feed', icon: Rss, label: 'Feed' },
                { path: '/bundle-tv', icon: Tv, label: 'Bundle TV' },
                { path: '/events', icon: Calendar, label: 'Events' },
                { path: '/golf-trips', icon: Plane, label: 'Travel' },
                { path: '/members', icon: Users, label: 'Members' },
                { path: '/network', icon: Briefcase, label: 'Network' },
                { path: '/gear', icon: Tag, label: 'Marketplace' },
              ].map(item => (
                <button
                  key={item.path}
                  data-testid={`mobile-more-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => { navigate(item.path); setShowMobileMore(false); }}
                  className={`relative flex flex-col items-center p-3 rounded-xl transition-all ${
                    isActive(item.path) ? 'bg-bundle-green/10 text-bundle-green' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <div className="relative">
                    <item.icon className="w-5 h-5 mb-1" />
                    {item.badge > 0 && (
                      <span className="absolute -top-1 -right-2 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] font-body font-medium">{item.label}</span>
                </button>
              ))}
            </div>

            {/* Member Support */}
            <button
              data-testid="mobile-more-member-support"
              onClick={async () => {
                setShowMobileMore(false);
                try { await fetch(`${API}/support/ping`, { method: 'POST', credentials: 'include' }); } catch (e) { console.error('support ping failed', e); }
                window.location.href = 'mailto:admin@thebundlegolfclubusa.com?subject=Bundle%20Golf%20Club%20-%20Member%20Support';
              }}
              className="w-full mt-3 flex items-center gap-3 px-3 py-3 rounded-xl bg-bundle-gold/10 border border-bundle-gold/20 text-bundle-gold-dark hover:bg-bundle-gold/20 transition-colors"
            >
              <LifeBuoy className="w-5 h-5" />
              <div className="text-left">
                <span className="font-body text-sm font-semibold">Member Support</span>
                <p className="font-body text-[10px] text-gray-500">Email the admin team</p>
              </div>
            </button>

            {/* Become an Admin Link */}
            <button
              data-testid="mobile-more-become-admin"
              onClick={() => { navigate('/become-admin'); setShowMobileMore(false); }}
              className="w-full mt-2 flex items-center gap-3 px-3 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 hover:bg-emerald-500/20 transition-colors"
            >
              <Star className="w-5 h-5" />
              <div className="text-left">
                <span className="font-body text-sm font-semibold">Become a Golf Club Director</span>
                <p className="font-body text-[10px] text-gray-500">Run your own tournaments</p>
              </div>
            </button>

            {/* Admin Tools (admin only) */}
            {user?.is_admin && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1">Director Tools</span>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <button
                    data-testid="mobile-admin-panel"
                    onClick={() => { navigate('/admin'); setShowMobileMore(false); }}
                    className="flex flex-col items-center p-3 rounded-xl bg-red-500/10 text-red-600 hover:bg-red-500/20"
                  >
                    <Shield className="w-5 h-5 mb-1" />
                    <span className="text-[10px] font-body font-medium">Director Panel</span>
                  </button>
                  <button
                    data-testid="mobile-admin-tournament-processor"
                    onClick={() => { navigate('/tournament-processor'); setShowMobileMore(false); }}
                    className="flex flex-col items-center p-3 rounded-xl bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20"
                  >
                    <Trophy className="w-5 h-5 mb-1" />
                    <span className="text-[10px] font-body font-medium">Tournament</span>
                  </button>
                  <button
                    data-testid="mobile-admin-course-mapper"
                    onClick={() => { navigate('/admin/course-mapper'); setShowMobileMore(false); }}
                    className="flex flex-col items-center p-3 rounded-xl bg-blue-500/10 text-blue-600 hover:bg-blue-500/20"
                  >
                    <MapPin className="w-5 h-5 mb-1" />
                    <span className="text-[10px] font-body font-medium">Courses</span>
                  </button>
                </div>
              </div>
            )}
            
            {/* Install App button — only show when running in browser, not already standalone */}
            {!isStandalone() && (
              <button
                data-testid="mobile-more-install-app"
                onClick={async () => {
                  setShowMobileMore(false);
                  const { available, outcome } = await triggerInstall();
                  if (available && outcome === 'accepted') return;
                  // No native prompt available — surface the top install banner with
                  // manual Chrome menu instructions by clearing the dismiss flag and
                  // reloading. The banner's Android fallback will guide the user.
                  localStorage.removeItem('pwa-install-dismissed');
                  window.scrollTo(0, 0);
                  window.location.reload();
                }}
                className="w-full mt-3 flex items-center gap-3 px-3 py-3 rounded-xl bg-bundle-green/5 border border-bundle-green/15 text-bundle-green hover:bg-bundle-green/10 transition-colors"
              >
                <Download className="w-5 h-5" />
                <div className="text-left">
                  <p className="text-sm font-body font-semibold">Install App</p>
                  <p className="text-[10px] font-body text-gray-500">Add to your home screen</p>
                </div>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation — Home | Games | + Create | Chat | Profile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-bundle-cream-dark z-50" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around py-1.5 px-1">
          <button
            data-testid="mobile-nav-home"
            onClick={() => navigate('/dashboard')}
            className={`flex flex-col items-center min-w-[56px] py-1 px-2 rounded-lg transition-colors ${
              isActive('/dashboard') ? 'text-bundle-green' : 'text-gray-400'
            }`}
          >
            <Home className={`w-5 h-5 ${isActive('/dashboard') ? 'stroke-[2.5]' : ''}`} />
            <span className="text-[10px] mt-0.5 font-body font-medium">Home</span>
          </button>

          <button
            data-testid="mobile-nav-games"
            onClick={() => navigate('/events')}
            className={`flex flex-col items-center min-w-[56px] py-1 px-2 rounded-lg transition-colors ${
              isActive('/events') ? 'text-bundle-green' : 'text-gray-400'
            }`}
          >
            <Calendar className={`w-5 h-5 ${isActive('/events') ? 'stroke-[2.5]' : ''}`} />
            <span className="text-[10px] mt-0.5 font-body font-medium">Games</span>
          </button>

          {/* Center CTA - Create Game */}
          <button
            data-testid="mobile-nav-create-game"
            onClick={() => navigate('/nearby-golfers')}
            className="flex flex-col items-center -mt-4"
          >
            <div className="w-14 h-14 bg-bundle-green rounded-full flex items-center justify-center shadow-lg shadow-bundle-green/30 border-4 border-white">
              <PlusCircle className="w-7 h-7 text-white" />
            </div>
            <span className="text-[10px] mt-0.5 font-body font-semibold text-bundle-green">Create</span>
          </button>

          <button
            data-testid="mobile-nav-chat"
            onClick={() => navigate('/chat')}
            className={`relative flex flex-col items-center min-w-[56px] py-1 px-2 rounded-lg transition-colors ${
              isActive('/chat') ? 'text-bundle-green' : 'text-gray-400'
            }`}
          >
            <div className="relative">
              <MessageCircle className={`w-5 h-5 ${isActive('/chat') ? 'stroke-[2.5]' : ''}`} />
              {chatUnreadCount > 0 && (
                <span className="absolute -top-1.5 -right-2 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                  {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                </span>
              )}
            </div>
            <span className="text-[10px] mt-0.5 font-body font-medium">Chat</span>
          </button>

          <button
            data-testid="mobile-nav-profile"
            onClick={() => navigate('/profile')}
            className={`flex flex-col items-center min-w-[56px] py-1 px-2 rounded-lg transition-colors ${
              isActive('/profile') ? 'text-bundle-green' : 'text-gray-400'
            }`}
          >
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-5 h-5 rounded-full object-cover" />
            ) : (
              <User className={`w-5 h-5 ${isActive('/profile') ? 'stroke-[2.5]' : ''}`} />
            )}
            <span className="text-[10px] mt-0.5 font-body font-medium">Profile</span>
          </button>
        </div>
      </nav>

      {/* Floating Chat Bubble — visible on all pages except /chat */}
      {!isActive('/chat') && (
        <button
          data-testid="floating-chat-bubble"
          onClick={() => navigate('/chat')}
          className="md:hidden fixed right-4 z-50 w-12 h-12 bg-bundle-green rounded-full shadow-lg shadow-bundle-green/30 flex items-center justify-center active:scale-95 transition-transform"
          style={{ bottom: 'calc(140px + env(safe-area-inset-bottom, 0px))' }}
        >
          <MessageCircle className="w-5 h-5 text-white" />
          {chatUnreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white" data-testid="floating-chat-badge">
              {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
            </span>
          )}
        </button>
      )}
    </>
  );
};

export const LogoBanner = ({ size = 'default' }) => {
  const sizes = {
    small: 'h-14',
    default: 'h-18',
    large: 'h-24'
  };

  return (
    <img 
      src="/logo.png" 
      alt="The Bundle Golf Club" 
      className={`${sizes[size]} w-auto`}
    />
  );
};
